import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Link } from "react-router-dom";
import { Radio, ArrowRight, Trophy } from "lucide-react";
import { UpdatedAgo } from "@/components/LiveTop5Board";

const SOURCE_LABEL = {
  live: "Live stats",
  simulated: "Simulated",
  projected: "Pre-kickoff",
};

/** Dashboard card: every slate of yours that is in flight, with rank and money position. */
export default function LiveNowCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: res } = await api.get("/users/me/live");
        if (!cancelled) setData(res);
      } catch (e) { /* card stays hidden */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data || !data.count) return null;

  return (
    <div className="border border-[#00FF66]/40 bg-[#00FF66]/[0.04] p-5" data-testid="live-now-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" />
            In play now
          </div>
          <h2 className="font-display text-3xl inline-flex items-center gap-2">
            <Radio size={20} className="text-[#00FF66]" /> LIVE ENTRIES
          </h2>
        </div>
        <div className="text-right">
          <div className="text-white/40 text-xs uppercase tracking-widest">Projected</div>
          <div className="font-display text-2xl text-[#00FF66]" data-testid="live-now-projected-total">
            ${data.entries.reduce((s, e) => s + (e.projected_payout || 0), 0).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {data.entries.map((e) => {
          const inMoney = (e.projected_payout || 0) > 0;
          return (
            <Link
              key={e.entry_id}
              to={`/contest/${e.contest_id}`}
              data-testid={`live-now-row-${e.entry_id}`}
              className="block border border-white/10 bg-[#0a0a0a] hover:border-[#00FF66]/40 p-3 transition-colors"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-white/40 text-[10px] uppercase tracking-widest">
                    {e.position} · {SOURCE_LABEL[e.source] || "Pre-kickoff"} ·{" "}
                    <UpdatedAgo at={e.updated_at} testId={`live-now-updated-${e.entry_id}`} />
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right">
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest">Rank</div>
                    <div className="font-display text-xl" data-testid={`live-now-rank-${e.entry_id}`}>
                      {e.rank ? `#${e.rank}` : "—"}
                      <span className="text-white/30 text-sm">/{e.field}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest">Hits</div>
                    <div className="font-display text-xl">{e.hits}/5</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest">
                      {e.practice ? "Practice" : "Projected"}
                    </div>
                    <div
                      className={`font-display text-xl ${inMoney ? "text-[#00FF66]" : "text-white/30"}`}
                      data-testid={`live-now-payout-${e.entry_id}`}
                    >
                      {e.practice ? "—" : inMoney ? `$${e.projected_payout.toFixed(2)}` : "$0.00"}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-white/30" />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {e.picks_detail?.map((p) => (
                  <span
                    key={p.player_id}
                    data-testid={`live-now-pick-${p.player_id}`}
                    className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                      p.in_top5
                        ? "border-[#00FF66]/50 text-[#00FF66] bg-[#00FF66]/10"
                        : "border-white/10 text-white/40"
                    }`}
                  >
                    {p.in_top5 && <Trophy size={9} className="inline mr-1 -mt-0.5" />}
                    {p.name}
                    {p.board_rank ? ` #${p.board_rank}` : ""}
                  </span>
                ))}
              </div>
              {!e.practice && e.field < 2 && (
                <div className="mt-2 text-[#FF9F0A] text-[10px] uppercase tracking-widest"
                  data-testid={`live-now-thin-field-${e.entry_id}`}>
                  Needs 1 more player to pay out — your entry refunds otherwise
                </div>
              )}
            </Link>
          );
        })}
      </div>
      {data.entries.some((e) => e.source === "simulated") && (
        <div className="mt-3 text-[#FF9F0A] text-[10px] uppercase tracking-widest">
          Practice slates run on a simulated feed — nothing is won or lost
        </div>
      )}
    </div>
  );
}
